import { useState } from "react";
import ArrowDownIcon from "@assets/icons/new/arrow-down_10px.svg?react";
import ArrowUpIcon from "@assets/icons/new/arrow-up_10px.svg?react";
import InfoSvg from "@assets/icons/new/info-circle_24px.svg?react";
import { FaroIconButton } from "@components/common/faro-icon-button";
import { SphereTooltip } from "@components/common/sphere-tooltip";
import { Dropdown, FaroSwitch } from "@faro-lotv/flat-ui";
import { assert } from "@faro-lotv/foundation";
import { Box, CircularProgress, Collapse, Stack, SvgIcon } from "@mui/material";
import { ExistingFileTypes } from "@pages/data-management/data-management-types";

interface Props {
  /** Flags for the occurring file types, incl. some combined meta information like hasLsDataV2. */
  existingFileTypes: ExistingFileTypes,
}

/** Component with UI elements for processing settings. */
export function ProcessOptions({ existingFileTypes }: Props): JSX.Element {
  // Todo
  const isLoading = false;
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [captureEnv, setCaptureEnv] = useState("standard");
  const [mountType, setMountType] = useState("handheld");

  // Currently only Orbis scans have processing options.
  assert(existingFileTypes.hasOrbis);

  return (
    <Stack gap="15px">
      {/* Section header for Orbis options */}
      <Stack
        direction="row"
        onClick={() => setIsCollapsed(!isCollapsed)}
        sx={{
          cursor: "pointer",
          fontWeight: "700",
      }}>
        <FaroIconButton
          buttonSize="24px"
          iconSize="16px"
          component={isCollapsed ? ArrowUpIcon : ArrowDownIcon}
        />

        <Box sx={{
          ml: "5px",
          mr: "7px",
          mt: "1px",
          fontSize: "14px",
          userSelect: "none",
        }}>
          Processing options – Orbis data
        </Box>

        <CircularProgress size={20} sx={{
          mt: "2px",
          visibility: isLoading ? "visible" : "hidden",
        }}/>
      </Stack>

      <Collapse in={!isCollapsed} sx={{ ml: "27px" }}>
        <Stack gap="10px">
          {/* Capture environment */}
          <Stack direction="row">
            <Box
              flexGrow={1}
              sx={{ fontSize: "14px" }}
            >
              Capture environment
            </Box>

            <Dropdown
              variant="underlined"
              sx={{
                boxShadow: "none",
                "&:hover": {
                  cursor: "pointer",
                  boxShadow: "none",
                },
              }}
              value={captureEnv}
              options={[
                {
                  key: "confined",
                  value: "confined",
                  label: "Confined",
                },
                {
                  key: "forest",
                  value: "forest",
                  label: "Forest",
                },
                {
                  key: "indoor",
                  value: "indoor",
                  label: "Indoor",
                },
                {
                  key: "openloop",
                  value: "openloop",
                  label: "Open loop",
                },
                {
                  key: "standard",
                  value: "standard",
                  label: "Standard",
                },
                {
                  key: "tunnel",
                  value: "tunnel",
                  label: "Tunnel",
                },
                {
                  key: "uav",
                  value: "uav",
                  label: "UAV",
                },
                {
                  key: "vehicle",
                  value: "vehicle",
                  label: "Vehicle",
                },
              ]}
              onChange={(e) => setCaptureEnv(e.target.value)}
            />
          </Stack>

          {/* Scanner mount type */}
          <Stack direction="row">
            <Box
              flexGrow={1}
              sx={{ fontSize: "14px" }}
            >
              Scanner mount type
            </Box>

            <Dropdown
              variant="underlined"
              sx={{
                boxShadow: "none",
                "&:hover": {
                  cursor: "pointer",
                  boxShadow: "none",
                },
              }}
              value={mountType}
              options={[
                {
                  key: "custom",
                  value: "custom",
                  label: "Custom",
                },
                {
                  key: "handheld",
                  value: "handheld",
                  label: "Handheld",
                },
                {
                  key: "monopod",
                  value: "monopod",
                  label: "Monopod",
                },
              ]}
              onChange={(e) => setMountType(e.target.value)}
            />
          </Stack>

          {/* Colorization */}
          <FaroSwitch
            label="Colorization"
            fullWidth={true}
            checked={false}
            disabled={false}
            onToggle={() => {
              //
            }}
          />

          {/* Remove transient points */}
          <FaroSwitch
            label={
              <Stack
                direction="row"
                gap="5px"
                sx={{
                  alignItems: "baseline",
              }}>
                <span>Remove transient points</span>

                <SphereTooltip
                  enterDelay={0}
                  title="Filters out moving objects from the point cloud."
                >
                  <SvgIcon
                    inheritViewBox
                    component={InfoSvg}
                    sx={{ mb: "-7px" }}
                  />
                </SphereTooltip>
              </Stack>
            }
            fullWidth={true}
            checked={false}
            disabled={false}
            onToggle={() => {
              //
            }}
          />

          {/* Detect and exclude outliers */}
          <FaroSwitch
            label="Detect and exclude outliers"
            fullWidth={true}
            checked={false}
            disabled={false}
            onToggle={() => {
              //
            }}
          />

          {/* Noise reduction */}
          <FaroSwitch
            label="Noise reduction"
            fullWidth={true}
            checked={false}
            disabled={false}
            onToggle={() => {
              //
            }}
          />
        </Stack>
      </Collapse>
    </Stack>
  );
}
